/**
 * ProductionMonitoringTable Component
 * 
 * Table component for Production Budget Monitoring with custom variance coloring
 * Variance interpretation: + (positive) = green (favorable), - (negative) = red (unfavorable)
 * 
 * Reuses UpkeepMonitoringTable structure but with production-specific logic
 */

import React, { useMemo } from 'react';
import {
    Box,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Text,
    useBreakpointValue,
    VStack,
    HStack,
    Divider,
    Skeleton,
    Stack,
    Badge,
    Collapse,
    IconButton,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { ProductionBudgetRow } from '../types';
import { formatNumber, formatVariance, transformToHierarchy } from '../../upkeep_monitoring/utils';
import { UpkeepBudgetHierarchy } from '../../upkeep_monitoring/types';

interface ProductionMonitoringTableProps {
    data: ProductionBudgetRow[];
    loading?: boolean;
}

/**
 * Get cell background color for highlighting (cell-specific)
 */
const getCellHighlightBg = (
    row: ProductionBudgetRow,
    columnType: 'mtdActual' | 'ytdActual'
): string | undefined => {
    if (columnType === 'mtdActual') {
        if (row.highlightMtdActualOrange) return 'orange.200';
        if (row.highlightMtdActualYellow) return 'yellow.200';
    }
    if (columnType === 'ytdActual') {
        if (row.highlightYtdActualOrange) return 'orange.200';
        if (row.highlightYtdActualYellow) return 'yellow.200';
    }
    return undefined;
};

/**
 * Get variance color for production (+ = green, - = red)
 */
const getProductionVarianceColor = (variance: number | null): string => {
    if (variance === null) return 'gray.400';
    if (variance > 0) return 'green.600';
    if (variance < 0) return 'red.600';
    return 'gray.600';
};

/**
 * Generate unique key for row
 */
const getRowKey = (row: ProductionBudgetRow): string => {
    return `${row.coaGroup}-${row.coa}-${row.activity}-${row.rowType}`;
};

// ===========================================
// MOBILE VIEW COMPONENTS
// ===========================================

/**
 * Mobile Row Component for single parameter row
 */
const MobileRowItem: React.FC<{ row: ProductionBudgetRow; isLast: boolean }> = ({ row, isLast }) => {
    const mtdActualHighlight = getCellHighlightBg(row, 'mtdActual');
    const ytdActualHighlight = getCellHighlightBg(row, 'ytdActual');
    
    return (
        <VStack align="stretch" spacing={2}>
            <HStack justify="space-between">
                <Text fontSize="sm" fontWeight="bold" color="gray.700">
                    {row.parameter}
                </Text>
            </HStack>

            <HStack justify="space-between" fontSize="xs">
                <VStack align="start" spacing={0}>
                    <Text color="gray.500">MTD Actual</Text>
                    <Box
                        bg={mtdActualHighlight}
                        px={mtdActualHighlight ? 1 : 0}
                        borderRadius="sm"
                    >
                        <Text fontWeight="semibold">{formatNumber(row.mtdActual)}</Text>
                    </Box>
                </VStack>
                <VStack align="start" spacing={0}>
                    <Text color="gray.500">MTD Budget</Text>
                    <Text fontWeight="semibold">{formatNumber(row.mtdBudget)}</Text>
                </VStack>
                <VStack align="start" spacing={0}>
                    <Text color="gray.500">Variance</Text>
                    <Text
                        fontWeight="semibold"
                        color={row.mtdVariance && row.mtdVariance > 0 ? 'green.500' : 'red.500'}
                    >
                        {formatVariance(row.mtdVariance)}
                    </Text>
                </VStack>
            </HStack>

            <HStack justify="space-between" fontSize="xs">
                <VStack align="start" spacing={0}>
                    <Text color="gray.500">YTD Actual</Text>
                    <Box
                        bg={ytdActualHighlight}
                        px={ytdActualHighlight ? 1 : 0}
                        borderRadius="sm"
                    >
                        <Text fontWeight="semibold">{formatNumber(row.ytdActual)}</Text>
                    </Box>
                </VStack>
                <VStack align="start" spacing={0}>
                    <Text color="gray.500">YTD Budget</Text>
                    <Text fontWeight="semibold">{formatNumber(row.ytdBudget)}</Text>
                </VStack>
                <VStack align="start" spacing={0}>
                    <Text color="gray.500">Variance</Text>
                    <Text
                        fontWeight="semibold"
                        color={row.ytdVariance && row.ytdVariance > 0 ? 'green.500' : 'red.500'}
                    >
                        {formatVariance(row.ytdVariance)}
                    </Text>
                </VStack>
            </HStack>

            {!isLast && <Divider borderStyle="dashed" />}
        </VStack>
    );
};

/**
 * Mobile Activity Card
 */
const MobileActivityCard: React.FC<{
    activity: string;
    rows: ProductionBudgetRow[];
}> = ({ activity, rows }) => {
    if (rows.length === 0) return null;

    return (
        <Box
            bg="white"
            p={3}
            borderRadius="md"
            border="1px"
            borderColor="gray.100"
        >
            <Text fontSize="xs" fontWeight="semibold" color="gray.700" mb={2}>
                {activity}
            </Text>
            <VStack align="stretch" spacing={2}>
                {rows.map((row, idx) => (
                    <MobileRowItem
                        key={getRowKey(row)}
                        row={row}
                        isLast={idx === rows.length - 1}
                    />
                ))}
            </VStack>
        </Box>
    );
};

/**
 * Mobile COA Card
 */
const MobileCoaCard: React.FC<{
    coa: string;
    activities: { activity: string; rows: ProductionBudgetRow[] }[];
}> = ({ coa, activities }) => {
    const [isOpen, setIsOpen] = React.useState(true);

    return (
        <Box
            bg="gray.50"
            borderRadius="lg"
            border="1px"
            borderColor="gray.200"
            overflow="hidden"
        >
            <HStack
                p={3}
                cursor="pointer"
                onClick={() => setIsOpen(!isOpen)}
                bg="blue.50"
                _hover={{ bg: 'blue.100' }}
            >
                <IconButton
                    aria-label={isOpen ? 'Collapse' : 'Expand'}
                    icon={isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
                    size="xs"
                    variant="ghost"
                />
                <Text fontSize="sm" fontWeight="semibold" color="blue.700">
                    {coa}
                </Text>
                <Badge colorScheme="blue" fontSize="xs">
                    {activities.length} activity
                </Badge>
            </HStack>
            <Collapse in={isOpen}>
                <VStack align="stretch" spacing={2} p={3}>
                    {activities.map((act) => (
                        <MobileActivityCard
                            key={`${coa}-${act.activity}`}
                            activity={act.activity}
                            rows={act.rows}
                        />
                    ))}
                </VStack>
            </Collapse>
        </Box>
    );
};

/**
 * Mobile COA Group Card
 */
const MobileCoaGroupCard: React.FC<{ 
    group: { 
        coaGroup: string; 
        coaList: Array<{ 
            coa: string; 
            activities: Array<{ activity: string; rows: ProductionBudgetRow[] }> 
        }> 
    } 
}> = ({ group }) => {
    const [isOpen, setIsOpen] = React.useState(true);

    return (
        <Box
            bg="white"
            borderRadius="xl"
            boxShadow="sm"
            border="1px"
            borderColor="gray.200"
            overflow="hidden"
        >
            <HStack
                p={4}
                cursor="pointer"
                onClick={() => setIsOpen(!isOpen)}
                bg="customGreen.500"
                _hover={{ bg: 'customGreen.600' }}
            >
                <IconButton
                    aria-label={isOpen ? 'Collapse' : 'Expand'}
                    icon={isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
                    size="sm"
                    variant="ghost"
                    color="white"
                    _hover={{ bg: 'whiteAlpha.200' }}
                />
                <Text fontSize="md" fontWeight="bold" color="white" flex={1}>
                    {group.coaGroup}
                </Text>
                <Badge colorScheme="whiteAlpha" color="white" fontSize="xs">
                    {group.coaList.length} kategori
                </Badge>
            </HStack>
            <Collapse in={isOpen}>
                <VStack align="stretch" spacing={3} p={3}>
                    {group.coaList.map((coaItem) => (
                        <MobileCoaCard
                            key={`${group.coaGroup}-${coaItem.coa}`}
                            coa={coaItem.coa}
                            activities={coaItem.activities}
                        />
                    ))}
                </VStack>
            </Collapse>
        </Box>
    );
};

// ===========================================
// DESKTOP TABLE COMPONENTS
// ===========================================

/**
 * Desktop Table Header
 */
const DesktopTableHeader: React.FC = () => (
    <Thead>
        <Tr bg="blue.700">
            <Th rowSpan={2} color="white" fontSize="xs" textTransform="uppercase" fontWeight="bold" borderRight="1px" borderColor="blue.600" textAlign="center" py={3} minW="150px">
                COA Group
            </Th>
            <Th rowSpan={2} color="white" fontSize="xs" textTransform="uppercase" fontWeight="bold" borderRight="1px" borderColor="blue.600" textAlign="center" py={3} minW="180px">
                Category
            </Th>
            <Th rowSpan={2} color="white" fontSize="xs" textTransform="uppercase" fontWeight="bold" borderRight="1px" borderColor="blue.600" textAlign="center" py={3} minW="200px">
                Activity
            </Th>
            <Th rowSpan={2} color="white" fontSize="xs" textTransform="uppercase" fontWeight="bold" borderRight="1px" borderColor="blue.600" textAlign="center" py={3} minW="100px">
                Parameter
            </Th>
            <Th colSpan={2} color="white" fontSize="xs" textTransform="uppercase" fontWeight="bold" borderRight="1px" borderColor="blue.600" textAlign="center" py={3}>
                MTD
            </Th>
            <Th rowSpan={2} color="white" fontSize="xs" textTransform="uppercase" fontWeight="bold" borderRight="1px" borderColor="blue.600" textAlign="center" py={3} minW="80px">
                Variance %
            </Th>
            <Th colSpan={2} color="white" fontSize="xs" textTransform="uppercase" fontWeight="bold" borderRight="1px" borderColor="blue.600" textAlign="center" py={3}>
                YTD
            </Th>
            <Th rowSpan={2} color="white" fontSize="xs" textTransform="uppercase" fontWeight="bold" textAlign="center" py={3} minW="80px">
                Variance %
            </Th>
        </Tr>
        <Tr bg="blue.700">
            <Th color="white" fontSize="xs" textTransform="uppercase" fontWeight="semibold" borderRight="1px" borderColor="blue.500" textAlign="center" py={2}>
                Actual
            </Th>
            <Th color="white" fontSize="xs" textTransform="uppercase" fontWeight="semibold" borderRight="1px" borderColor="blue.500" textAlign="center" py={2}>
                Budget
            </Th>
            <Th color="white" fontSize="xs" textTransform="uppercase" fontWeight="semibold" borderRight="1px" borderColor="blue.500" textAlign="center" py={2}>
                Actual
            </Th>
            <Th color="white" fontSize="xs" textTransform="uppercase" fontWeight="semibold" borderRight="1px" borderColor="blue.500" textAlign="center" py={2}>
                Budget
            </Th>
        </Tr>
    </Thead>
);

/**
 * Desktop Data Row
 */
const DesktopDataRow: React.FC<{
    row: ProductionBudgetRow;
    showCoaGroup: boolean;
    showCoa: boolean;
    showActivity: boolean;
    coaGroupRowSpan: number;
    coaRowSpan: number;
    activityRowSpan: number;
    isCoaGroupExpanded: boolean;
    isCoaExpanded: boolean;
    onToggleCoaGroup: () => void;
    onToggleCoa: () => void;
}> = ({ 
    row, 
    showCoaGroup, 
    showCoa, 
    showActivity, 
    coaGroupRowSpan, 
    coaRowSpan, 
    activityRowSpan,
    isCoaGroupExpanded,
    isCoaExpanded,
    onToggleCoaGroup,
    onToggleCoa
}) => {
    const mtdActualBg = getCellHighlightBg(row, 'mtdActual');
    const ytdActualBg = getCellHighlightBg(row, 'ytdActual');

    return (
        <Tr _hover={{ bg: 'gray.50' }}>
            {showCoaGroup && (
                <Td rowSpan={coaGroupRowSpan} fontSize="xs" fontWeight="bold" color="gray.700" borderRight="1px" borderColor="gray.200" py={2} px={2} verticalAlign="top">
                    <HStack spacing={2}>
                        <IconButton
                            aria-label={isCoaGroupExpanded ? 'Collapse' : 'Expand'}
                            icon={isCoaGroupExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                            size="xs"
                            variant="ghost"
                            onClick={onToggleCoaGroup}
                        />
                        <Text>{row.coaGroup}</Text>
                    </HStack>
                </Td>
            )}

            {showCoa && (
                <Td rowSpan={coaRowSpan} fontSize="xs" fontWeight="semibold" color="gray.700" borderRight="1px" borderColor="gray.200" py={2} px={2} verticalAlign="top">
                    <HStack spacing={2}>
                        <IconButton
                            aria-label={isCoaExpanded ? 'Collapse' : 'Expand'}
                            icon={isCoaExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                            size="xs"
                            variant="ghost"
                            onClick={onToggleCoa}
                        />
                        <Text>{row.coa}</Text>
                    </HStack>
                </Td>
            )}

            {showActivity && (
                <Td rowSpan={activityRowSpan} fontSize="xs" fontWeight="medium" color="gray.700" borderRight="1px" borderColor="gray.200" py={2} verticalAlign="top">
                    {row.activity}
                </Td>
            )}

            <Td fontSize="xs" color={row.rowType === 'hk_per_uom' ? 'blue.600' : 'gray.600'} fontWeight={row.rowType === 'hk_per_uom' ? 'semibold' : 'normal'} borderRight="1px" borderColor="gray.200" py={1}>
                {row.parameter}
            </Td>

            <Td fontSize="xs" textAlign="right" borderRight="1px" borderColor="gray.200" py={1} bg={mtdActualBg} fontWeight={mtdActualBg ? 'semibold' : 'normal'}>
                {formatNumber(row.mtdActual)}
            </Td>

            <Td fontSize="xs" textAlign="right" borderRight="1px" borderColor="gray.200" py={1}>
                {formatNumber(row.mtdBudget)}
            </Td>

            <Td fontSize="xs" textAlign="right" fontWeight="medium" color={getProductionVarianceColor(row.mtdVariance)} borderRight="1px" borderColor="gray.200" py={1}>
                {formatVariance(row.mtdVariance)}
            </Td>

            <Td fontSize="xs" textAlign="right" borderRight="1px" borderColor="gray.200" py={1} bg={ytdActualBg} fontWeight={ytdActualBg ? 'semibold' : 'normal'}>
                {formatNumber(row.ytdActual)}
            </Td>

            <Td fontSize="xs" textAlign="right" borderRight="1px" borderColor="gray.200" py={1}>
                {formatNumber(row.ytdBudget)}
            </Td>

            <Td fontSize="xs" textAlign="right" fontWeight="medium" color={getProductionVarianceColor(row.ytdVariance)} py={1}>
                {formatVariance(row.ytdVariance)}
            </Td>
        </Tr>
    );
};

/**
 * Desktop Table Body with Collapse State
 */
/**
 * Desktop Table Body with Collapse State
 */
const DesktopTableBody: React.FC<{ 
    hierarchy: Array<{ 
        coaGroup: string; 
        coaList: Array<{ 
            coa: string; 
            activities: Array<{ activity: string; rows: ProductionBudgetRow[] }> 
        }> 
    }>
}> = ({ hierarchy }) => {
    // Menggunakan asumsi default: jika key tidak ada, nilainya true (terbuka)
    const [expandedGroups, setExpandedGroups] = React.useState<Record<string, boolean>>({});
    const [expandedCoas, setExpandedCoas] = React.useState<Record<string, boolean>>({});

    const toggleCoaGroup = (coaGroup: string) => {
        setExpandedGroups(prev => ({
            ...prev,
            [coaGroup]: !(prev[coaGroup] ?? true)
        }));
    };

    const toggleCoa = (coaGroup: string, coa: string) => {
        const coaKey = `${coaGroup}::${coa}`;
        setExpandedCoas(prev => ({
            ...prev,
            [coaKey]: !(prev[coaKey] ?? true)
        }));
    };

    const calculateCoaRowCount = (activities: Array<{ activity: string; rows: ProductionBudgetRow[] }>): number => {
        return activities.reduce((sum, act) => sum + act.rows.length, 0);
    };

    const allRows: React.ReactNode[] = [];
    
    hierarchy.forEach((group) => {
        const isGroupExpanded = expandedGroups[group.coaGroup] ?? true;

        // Hitung total baris aktual untuk rowSpan sel COA Group
        const visibleGroupRowCount = isGroupExpanded 
            ? group.coaList.reduce((sum, coaItem) => {
                const coaKey = `${group.coaGroup}::${coaItem.coa}`;
                const isCoaExpanded = expandedCoas[coaKey] ?? true;
                return sum + (isCoaExpanded ? calculateCoaRowCount(coaItem.activities) : 1);
            }, 0)
            : 1;

        let isFirstRowInGroup = true;

        if (!isGroupExpanded) {
            // Render 1 baris pengganti (placeholder) ketika COA Group ditutup
            allRows.push(
                <Tr key={`collapsed-group-${group.coaGroup}`} _hover={{ bg: 'gray.50' }}>
                    <Td fontSize="xs" fontWeight="bold" color="gray.700" borderRight="1px" borderColor="gray.200" py={2} px={2}>
                        <HStack spacing={2}>
                            <IconButton
                                aria-label="Expand"
                                icon={<ChevronRightIcon />}
                                size="xs"
                                variant="ghost"
                                onClick={() => toggleCoaGroup(group.coaGroup)}
                            />
                            <Text>{group.coaGroup}</Text>
                        </HStack>
                    </Td>
                    <Td colSpan={9} fontSize="xs" color="gray.500" fontStyle="italic" py={2} px={3}>
                        {group.coaList.length} kategori 
                    </Td>
                </Tr>
            );
            return; // Lanjut ke iterasi grup berikutnya
        }
        
        group.coaList.forEach((coaItem) => {
            const coaKey = `${group.coaGroup}::${coaItem.coa}`;
            const isCoaExpanded = expandedCoas[coaKey] ?? true;
            const coaRowCount = calculateCoaRowCount(coaItem.activities);
            
            let isFirstRowInCoa = true;

            if (!isCoaExpanded) {
                // Render 1 baris pengganti (placeholder) ketika spesifik COA ditutup
                allRows.push(
                    <Tr key={`collapsed-coa-${coaKey}`} _hover={{ bg: 'gray.50' }}>
                        {isFirstRowInGroup && (
                            <Td rowSpan={visibleGroupRowCount} fontSize="xs" fontWeight="bold" color="gray.700" borderRight="1px" borderColor="gray.200" py={2} px={2} verticalAlign="top">
                                <HStack spacing={2}>
                                    <IconButton
                                        aria-label="Collapse"
                                        icon={<ChevronDownIcon />}
                                        size="xs"
                                        variant="ghost"
                                        onClick={() => toggleCoaGroup(group.coaGroup)}
                                    />
                                    <Text>{group.coaGroup}</Text>
                                </HStack>
                            </Td>
                        )}
                        <Td fontSize="xs" fontWeight="semibold" color="gray.700" borderRight="1px" borderColor="gray.200" py={2} px={2}>
                            <HStack spacing={2}>
                                <IconButton
                                    aria-label="Expand"
                                    icon={<ChevronRightIcon />}
                                    size="xs"
                                    variant="ghost"
                                    onClick={() => toggleCoa(group.coaGroup, coaItem.coa)}
                                />
                                <Text>{coaItem.coa}</Text>
                            </HStack>
                        </Td>
                        <Td colSpan={8} fontSize="xs" color="gray.500" fontStyle="italic" py={2} px={3}>
                            {coaItem.activities.length} aktivitas 
                        </Td>
                    </Tr>
                );
                isFirstRowInGroup = false;
                return; // Lanjut ke iterasi COA berikutnya
            }
            
            coaItem.activities.forEach((activityItem) => {
                const activityRowCount = activityItem.rows.length;
                let isFirstRowInActivity = true;
                
                activityItem.rows.forEach((row) => {
                    allRows.push(
                        <DesktopDataRow
                            key={getRowKey(row)}
                            row={row}
                            showCoaGroup={isFirstRowInGroup}
                            showCoa={isFirstRowInCoa}
                            showActivity={isFirstRowInActivity}
                            coaGroupRowSpan={visibleGroupRowCount}
                            coaRowSpan={coaRowCount}
                            activityRowSpan={activityRowCount}
                            isCoaGroupExpanded={isGroupExpanded}
                            isCoaExpanded={isCoaExpanded}
                            onToggleCoaGroup={() => toggleCoaGroup(group.coaGroup)}
                            onToggleCoa={() => toggleCoa(group.coaGroup, coaItem.coa)}
                        />
                    );
                    isFirstRowInGroup = false;
                    isFirstRowInCoa = false;
                    isFirstRowInActivity = false;
                });
            });
        });
    });

    if (allRows.length === 0) {
        return (
            <Tbody>
                <Tr>
                    <Td colSpan={10} textAlign="center" py={8}>
                        <Text color="gray.500">Tidak ada data</Text>
                    </Td>
                </Tr>
            </Tbody>
        );
    }

    return <Tbody>{allRows}</Tbody>;
};

/**
 * Desktop Loading Skeleton
 */
const DesktopLoadingSkeleton: React.FC = () => (
    <Tbody>
        {[...Array(10)].map((_, idx) => (
            <Tr key={idx}>
                {[...Array(10)].map((_, colIdx) => (
                    <Td key={colIdx} py={2}>
                        <Skeleton height="20px" />
                    </Td>
                ))}
            </Tr>
        ))}
    </Tbody>
);

/**
 * Main ProductionMonitoringTable Component
 */
const ProductionMonitoringTable: React.FC<ProductionMonitoringTableProps> = ({ data, loading }) => {
    const isMobile = useBreakpointValue({ base: true, md: false });

    const hierarchy = useMemo(() => {
        const transformed = transformToHierarchy(data as any);
        return transformed as Array<{ 
            coaGroup: string; 
            coaList: Array<{ 
                coa: string; 
                activities: Array<{ activity: string; rows: ProductionBudgetRow[] }> 
            }> 
        }>;
    }, [data]);

    if (loading) {
        if (isMobile) {
            return (
                <VStack spacing={3} align="stretch">
                    {[...Array(5)].map((_, idx) => (
                        <Box key={idx} bg="white" p={3} borderRadius="md" border="1px" borderColor="gray.100">
                            <Skeleton height="20px" mb={2} />
                            <Stack spacing={2}>
                                <Skeleton height="15px" />
                                <Skeleton height="15px" />
                            </Stack>
                        </Box>
                    ))}
                </VStack>
            );
        }

        return (
            <Box overflowX="auto" bg="white" borderRadius="lg" boxShadow="sm">
                <Table size="sm" variant="simple">
                    <DesktopTableHeader />
                    <DesktopLoadingSkeleton />
                </Table>
            </Box>
        );
    }

    if (isMobile) {
        return (
            <VStack spacing={3} align="stretch">
                {hierarchy.map((group) => (
                    <MobileCoaGroupCard key={group.coaGroup} group={group} />
                ))}
            </VStack>
        );
    }

    return (
        <Box overflowX="auto" bg="white" borderRadius="lg" boxShadow="sm">
            <Table size="sm" variant="simple">
                <DesktopTableHeader />
                <DesktopTableBody hierarchy={hierarchy} />
            </Table>
        </Box>
    );
};

export default ProductionMonitoringTable;

